import React, { useState, useEffect } from 'react';
import { Course, UserRole } from '../../types/lms.types';

interface LiveClassMockProps {
  course: Course;
  userRole: UserRole;
  onLeave: () => void;
}

const LiveClassMock: React.FC<LiveClassMockProps> = ({ course, userRole, onLeave }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    { id: 1, user: 'Dr. Sarah Johnson', message: 'Welcome to today\'s audio class!', time: '10:00', isTeacher: true },
    { id: 2, user: 'Alice', message: 'Good morning everyone!', time: '10:01', isTeacher: false },
    { id: 3, user: 'Bob', message: 'Ready for today\'s session', time: '10:02', isTeacher: false }
  ]);
  const [participants] = useState([
    { name: 'Dr. Sarah Johnson', role: 'teacher', isMuted: false, isListening: true },
    { name: 'Alice Johnson', role: 'student', isMuted: true, isListening: false },
    { name: 'Bob Smith', role: 'student', isMuted: true, isListening: false },
    { name: 'Carol Davis', role: 'student', isMuted: false, isListening: true },
    { name: 'You', role: userRole, isMuted: isMuted, isListening: isListening }
  ]);
  const [currentTime, setCurrentTime] = useState('00:00');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      setCurrentTime(`${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Audio detection simulation when mic is on
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!isMuted) {
      interval = setInterval(() => {
        setIsListening(true);
        setTimeout(() => setIsListening(false), 800);
      }, 2500);
    } else {
      setIsListening(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isMuted]);

  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      const newMessage = {
        id: chatMessages.length + 1,
        user: userRole === 'teacher' ? course.teacher : 'You',
        message: chatMessage,
        time: currentTime,
        isTeacher: userRole === 'teacher'
      };
      setChatMessages([...chatMessages, newMessage]);
      setChatMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white flex flex-col">
      {/* Enhanced Header */}
      <div className="bg-black/30 backdrop-blur-sm p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-xl">🎓</span>
            </div>
            <div>
              <h1 className="text-xl font-bold">{course.name} - Audio Class</h1>
              <p className="text-sm text-gray-300">Instructor: {course.teacher} • {participants.length} participants</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-red-600/20 px-3 py-2 rounded-full border border-red-500/30">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">LIVE AUDIO</span>
            <span className="text-sm">{currentTime}</span>
          </div>
          <button
            onClick={onLeave}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
          >
            {userRole === 'teacher' ? 'End Class' : 'Leave Class'}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Main Audio Area */}
        <div className="flex-1 p-6">
          {/* Teacher/Main Speaker Section */}
          <div className="bg-gradient-to-br from-blue-800/50 to-purple-800/50 rounded-2xl p-8 mb-6 backdrop-blur-sm border border-white/10">
            <div className="text-center">
              <div className={`relative w-32 h-32 mx-auto mb-6 ${isListening && userRole === 'teacher' ? 'animate-pulse' : ''}`}>
                <div className={`w-32 h-32 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center transition-all duration-300 ${isListening && userRole === 'teacher' ? 'ring-4 ring-green-400 ring-opacity-75 scale-110' : ''}`}>
                  <span className="text-4xl font-bold">
                    {userRole === 'teacher' ? course.teacher.charAt(0) : 'YOU'}
                  </span>
                </div>
                {isListening && userRole === 'teacher' && (
                  <div className="absolute inset-0 rounded-full border-4 border-green-400 animate-ping"></div>
                )}
              </div>
              <h2 className="text-2xl font-bold mb-2">{userRole === 'teacher' ? course.teacher : 'You'}</h2>
              <p className="text-gray-300 mb-4">
                {userRole === 'teacher' ? 'Instructor' : 'Student'}
              </p>
              
              {/* Voice Activity Indicator */}
              {isListening && (
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="flex space-x-1">
                    <div className="w-2 h-6 bg-green-400 rounded animate-pulse" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-8 bg-green-400 rounded animate-pulse" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-4 bg-green-400 rounded animate-pulse" style={{animationDelay: '300ms'}}></div>
                    <div className="w-2 h-7 bg-green-400 rounded animate-pulse" style={{animationDelay: '450ms'}}></div>
                    <div className="w-2 h-5 bg-green-400 rounded animate-pulse" style={{animationDelay: '600ms'}}></div>
                  </div>
                  <span className="text-green-400 text-sm font-medium">Speaking...</span>
                </div>
              )}
            </div>
          </div>

          {/* Participants Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {participants.slice(0, -1).map((participant, index) => (
              <div key={index} className="bg-black/30 backdrop-blur-sm rounded-xl p-4 border border-white/10 relative">
                <div className="text-center">
                  <div className={`w-16 h-16 bg-gradient-to-r from-gray-600 to-gray-700 rounded-full flex items-center justify-center mb-3 mx-auto transition-all duration-300 ${participant.isListening ? 'ring-2 ring-green-400 ring-opacity-75' : ''}`}>
                    <span className="text-lg font-medium">{participant.name.charAt(0)}</span>
                  </div>
                  <p className="text-sm truncate font-medium">{participant.name}</p>
                  <p className="text-xs text-gray-400">{participant.role}</p>
                </div>
                
                {/* Audio Status Indicators */}
                <div className="absolute top-2 right-2 flex gap-1">
                  {participant.isMuted ? (
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                      <span className="text-xs">🔇</span>
                    </div>
                  ) : (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${participant.isListening ? 'bg-green-600 animate-pulse' : 'bg-blue-600'}`}>
                      <span className="text-xs">🎤</span>
                    </div>
                  )}
                </div>
                
                {participant.isListening && (
                  <div className="absolute bottom-2 left-2 flex space-x-1">
                    <div className="w-1 h-3 bg-green-400 rounded animate-pulse" style={{animationDelay: '0ms'}}></div>
                    <div className="w-1 h-4 bg-green-400 rounded animate-pulse" style={{animationDelay: '150ms'}}></div>
                    <div className="w-1 h-2 bg-green-400 rounded animate-pulse" style={{animationDelay: '300ms'}}></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Enhanced Audio Controls */}
          <div className="flex justify-center">
            <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-6 border border-white/20">
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isMuted 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : isListening 
                      ? 'bg-green-600 hover:bg-green-700 text-white ring-4 ring-green-400 ring-opacity-50 animate-pulse' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                <span className="text-2xl">
                  {isMuted ? '🔇' : '🎤'}
                </span>
                {isListening && !isMuted && (
                  <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping"></div>
                )}
              </button>
              
              <div className="text-center">
                <p className="text-sm text-gray-300 mb-1">Audio Status</p>
                <p className={`text-sm font-medium ${isMuted ? 'text-red-400' : isListening ? 'text-green-400' : 'text-blue-400'}`}>
                  {isMuted ? 'Muted' : isListening ? 'Speaking' : 'Listening'}
                </p>
              </div>

              {userRole === 'teacher' && (
                <button className="w-16 h-16 bg-purple-600 hover:bg-purple-700 rounded-full flex items-center justify-center transition-colors">
                  <span className="text-2xl">📋</span>
                </button>
              )}
              
              <button className="w-16 h-16 bg-yellow-600 hover:bg-yellow-700 rounded-full flex items-center justify-center transition-colors">
                <span className="text-2xl">✋</span>
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Chat Sidebar */}
        <div className="w-full lg:w-96 bg-black/30 backdrop-blur-sm flex flex-col border-l border-white/10">
          <div className="p-4 border-b border-white/10">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <span>💬</span>
              Class Chat
              <span className="text-sm text-gray-400">({chatMessages.length})</span>
            </h3>
          </div>
          
          <div className="flex-1 p-4 overflow-y-auto space-y-3">
            {chatMessages.map((msg) => (
              <div key={msg.id} className={`p-3 rounded-lg ${msg.isTeacher ? 'bg-blue-600/20 border-l-4 border-blue-500' : 'bg-gray-700/50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`font-medium text-sm ${msg.isTeacher ? 'text-blue-400' : 'text-white'}`}>
                    {msg.user}
                  </span>
                  <span className="text-xs text-gray-400">{msg.time}</span>
                </div>
                <p className="text-sm text-gray-200">{msg.message}</p>
              </div>
            ))}
          </div>
          
          <div className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleSendMessage}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveClassMock;
